/**
 * SubItemEditorPage - Full-page sub-item editor
 *
 * Replaces the main ItemEditor view when editing/adding a sub-item.
 * Has a back arrow to return to the parent item.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { ThreeColumnEditor } from '@/components/layout';
import { useItemStore, useAuthStore, useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import {
  ItemFormFieldsCol1,
  ItemFormFieldsCol2,
  AttachmentsField,
  getDefaultFormValues,
  type ItemFormData,
} from './components/fields';
import {
  subItemToFormData,
  pendingInputToFormData,
  formDataToCreateSubItemInput,
  formDataToUpdateSubItemInput,
  formatEditorTimestamp,
} from './utils/formHelpers';
import {
  captureOriginalStateFromSubItem,
  captureOriginalStateFromDefaults,
} from './utils/unsavedChanges';
import { useUnsavedChanges } from './hooks';
import { createSubItemDraft, type SubItemDraft } from '@/services';
import type {
  SubItem,
  Item,
  ItemType,
  CreateSubItemInput,
  Attachment,
} from '@perfin/shared';
import { getItemTypeLabel } from '@perfin/shared';

// ============================================================================
// TYPES
// ============================================================================

interface SubItemEditorPageProps {
  parentItem?: Item;         // parent item for field inheritance
  parentFormData?: ItemFormData; // parent's current form values (fallback for unsaved parents)
  parentId?: string;         // undefined if parent not yet saved
  parentCreatedBy?: string;  // createdBy of parent item (for Firestore path)
  mode: 'edit' | 'create';
  subItem?: SubItem;         // for edit mode
  defaultType?: ItemType;    // for create mode
  pendingInput?: CreateSubItemInput; // for editing pending sub-items (pre-populate form)
  onBack: () => void;
  onSave: (input?: CreateSubItemInput) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function SubItemEditorPage({
  parentItem,
  parentFormData,
  parentId,
  parentCreatedBy,
  mode,
  subItem,
  defaultType = 'task',
  pendingInput,
  onBack,
  onSave,
}: SubItemEditorPageProps) {
  const { user, profile } = useAuthStore();
  const { addSubItem, editSubItem, addSubItemAttachment, removeSubItemAttachment, removeSubItem } = useItemStore();
  const { categories, members, entities, accounts } = useSettingsStore();
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  const isEditing = mode === 'edit' && !!subItem;
  const currentType = pendingInput?.type || subItem?.type || defaultType;

  // Check if sub-item is completed (task uses 'complete', others use 'final')
  const isSubItemCompleted = subItem ? (
    subItem.type === 'task'
      ? (subItem as unknown as { status?: string }).status === 'complete'
      : (subItem as unknown as { status?: string }).status === 'final'
  ) : false;

  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<SubItemDraft | null>(null);
  const draftInitialized = useRef(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [createdSubItemId, setCreatedSubItemId] = useState<string | null>(null);

  // Effective sub-item ID: use existing ID or the one we just created
  const effectiveSubItemId = subItem?.id || createdSubItemId;

  // Determine initial form values - inherit from parent item when creating new sub-item
  const getInitialFormValues = () => {
    if (subItem) return subItemToFormData(subItem);
    if (pendingInput) return pendingInputToFormData(pendingInput, user?.uid);

    // Start with defaults for the current type
    const defaults = { ...getDefaultFormValues(currentType), owner: user?.uid || 'None' };

    // Inherit fields from parent item if available
    if (parentItem) {
      // Common fields inherited from parent
      if (parentItem.category) defaults.category = parentItem.category;
      if (parentItem.subCategory) defaults.subCategory = parentItem.subCategory;
      if (parentItem.owner) defaults.owner = parentItem.owner;
      if (parentItem.priority) defaults.priority = parentItem.priority;

      // Inherit dueDate based on parent's recurrence state
      const parentRecurrence = (parentItem as unknown as { recurrence?: string }).recurrence;
      if (!parentRecurrence || parentRecurrence === 'none') {
        const parentDueDate = (parentItem as unknown as { dueDate?: { toDate: () => Date } }).dueDate;
        if (parentDueDate) {
          defaults.dueDate = parentDueDate.toDate();
        } else {
          defaults.dueDate = null;
        }
      } else {
        // Recurring parent: inherit startDate as sub-item dueDate
        const parentStartDate = (parentItem as unknown as { startDate?: { toDate: () => Date } }).startDate;
        if (parentStartDate) {
          defaults.dueDate = parentStartDate.toDate();
        } else {
          defaults.dueDate = null;
        }
      }

      // Type-specific inheritance
      if (currentType === 'transaction' && parentItem.type === 'transaction') {
        const txnParent = parentItem as unknown as {
          transactionType?: 'expense' | 'income';
          currency?: string;
          entityId?: string;
          accountId?: string;
        };
        if (txnParent.transactionType) defaults.transactionType = txnParent.transactionType;
        if (txnParent.currency) defaults.currency = txnParent.currency;
        if (txnParent.entityId) defaults.entityId = txnParent.entityId;
        if (txnParent.accountId) defaults.accountId = txnParent.accountId;
      }

      if (currentType === 'note' && parentItem.type === 'note') {
        const noteParent = parentItem as unknown as { color?: string; tags?: string[] };
        if (noteParent.color) defaults.color = noteParent.color;
        if (noteParent.tags?.length) defaults.tags = [...noteParent.tags];
      }
    } else if (parentFormData) {
      // Fallback: inherit from parent's unsaved form values
      if (parentFormData.category) defaults.category = parentFormData.category;
      if (parentFormData.subCategory) defaults.subCategory = parentFormData.subCategory;
      if (parentFormData.owner) defaults.owner = parentFormData.owner;
      if (parentFormData.priority) defaults.priority = parentFormData.priority;

      // Inherit dueDate based on parent's recurrence state
      if (!parentFormData.recurrence || parentFormData.recurrence === 'none') {
        if (parentFormData.dueDate) {
          defaults.dueDate = parentFormData.dueDate;
        } else {
          defaults.dueDate = null;
        }
      } else {
        // Recurring parent: inherit startDate as sub-item dueDate
        if (parentFormData.startDate) {
          defaults.dueDate = parentFormData.startDate;
        } else {
          defaults.dueDate = null;
        }
      }

      // Type-specific inheritance
      if (currentType === 'transaction') {
        if (parentFormData.transactionType) defaults.transactionType = parentFormData.transactionType;
        if (parentFormData.currency) defaults.currency = parentFormData.currency;
        if (parentFormData.entityId) defaults.entityId = parentFormData.entityId;
        if (parentFormData.accountId) defaults.accountId = parentFormData.accountId;
      }

      if (currentType === 'note') {
        if (parentFormData.color) defaults.color = parentFormData.color;
        if (parentFormData.tags?.length) defaults.tags = [...parentFormData.tags];
      }
    }

    return defaults;
  };

  const form = useForm<ItemFormData>({
    defaultValues: getInitialFormValues(),
  });

  // Initialize draft with original state
  useEffect(() => {
    if (draftInitialized.current) return;
    draftInitialized.current = true;

    const initialFormValues = getInitialFormValues();
    const newDraft = createSubItemDraft(currentType, parentId, subItem?.id);

    if (subItem) {
      // Editing existing sub-item
      newDraft.originalState = captureOriginalStateFromSubItem(initialFormValues, subItem);
    } else {
      // Creating new sub-item or editing pending
      newDraft.originalState = captureOriginalStateFromDefaults(initialFormValues);
    }
    newDraft.removedAttachmentIds = [];
    setDraft(newDraft);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset form when source data changes (not on draft/parentItem updates)
  useEffect(() => {
    if (subItem) {
      const formData = subItemToFormData(subItem);
      form.reset(formData);
    } else if (pendingInput) {
      form.reset(pendingInputToFormData(pendingInput, user?.uid));
    } else if (!createdSubItemId) {
      form.reset(getInitialFormValues());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subItem, pendingInput, currentType, form, user?.uid]);

  // Track whether settings have been applied after they load
  const settingsAppliedRef = useRef<{
    categories: boolean;
    members: boolean;
    entities: boolean;
    accounts: boolean;
  }>({ categories: false, members: false, entities: false, accounts: false });

  // Reset settings applied flags when source data changes
  useEffect(() => {
    settingsAppliedRef.current = { categories: false, members: false, entities: false, accounts: false };
  }, [subItem?.id, pendingInput]);

  // Re-apply dynamic dropdown values when settings load
  // This handles the race condition where form.reset runs before settings are loaded
  useEffect(() => {
    // Get expected values from subItem or pendingInput
    const sourceData = subItem || pendingInput;
    if (!sourceData) return;

    const savedCategory = (sourceData as { category?: string }).category;
    const savedSubCategory = (sourceData as { subCategory?: string }).subCategory;
    const savedOwner = (sourceData as { owner?: string }).owner || user?.uid;
    const savedEntityId = (sourceData as { entityId?: string }).entityId;
    const savedAccountId = (sourceData as { accountId?: string }).accountId;

    // Re-apply category when categories load (only once)
    if (categories.length > 0 && savedCategory && !settingsAppliedRef.current.categories) {
      const categoryExists = categories.some(c => c.id === savedCategory) || savedCategory === 'None';
      if (categoryExists) {
        form.setValue('category', savedCategory, { shouldDirty: false, shouldTouch: false });
        settingsAppliedRef.current.categories = true;

        // Also re-apply subCategory if we have one
        if (savedSubCategory) {
          const parentCategory = categories.find(c => c.id === savedCategory);
          const subCategoryExists = parentCategory?.subCategories?.some(s => s.id === savedSubCategory) || savedSubCategory === 'None';
          if (subCategoryExists) {
            form.setValue('subCategory', savedSubCategory, { shouldDirty: false, shouldTouch: false });
          }
        }
      }
    }

    // Re-apply owner when members load (only once)
    if (members.length > 0 && savedOwner && !settingsAppliedRef.current.members) {
      const ownerExists = members.some(m => m.userId === savedOwner) || savedOwner === 'None';
      if (ownerExists) {
        form.setValue('owner', savedOwner, { shouldDirty: false, shouldTouch: false });
        settingsAppliedRef.current.members = true;
      }
    }

    // Re-apply entityId when entities load (only once)
    if (entities.length > 0 && savedEntityId && !settingsAppliedRef.current.entities) {
      const entityExists = entities.some(e => e.id === savedEntityId) || savedEntityId === 'None';
      if (entityExists) {
        form.setValue('entityId', savedEntityId, { shouldDirty: false, shouldTouch: false });
        settingsAppliedRef.current.entities = true;
      }
    }

    // Re-apply accountId when accounts load (only once)
    if (accounts.length > 0 && savedAccountId && savedEntityId && !settingsAppliedRef.current.accounts) {
      const accountExists = accounts.some(a => a.id === savedAccountId && a.entityId === savedEntityId) || savedAccountId === 'None';
      if (accountExists) {
        form.setValue('accountId', savedAccountId, { shouldDirty: false, shouldTouch: false });
        settingsAppliedRef.current.accounts = true;
      }
    }
  }, [categories, members, entities, accounts, form, subItem, pendingInput, user?.uid]);

  const handleFileDrop = (files: File[]) => {
    setPendingAttachments((prev) => [...prev, ...files]);
  };

  const handleRemovePendingFile = (index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Defer attachment removal - track IDs to delete on save
  const handleRemoveExistingAttachment = useCallback((attachment: Attachment) => {
    setDraft((currentDraft) => {
      if (!currentDraft) return null;
      // Add to removed list if not already there
      if (currentDraft.removedAttachmentIds.includes(attachment.id)) {
        return currentDraft;
      }
      return {
        ...currentDraft,
        removedAttachmentIds: [...currentDraft.removedAttachmentIds, attachment.id],
        lastModified: Date.now(),
      };
    });
  }, []);

  // Filter visible attachments (exclude ones marked for removal)
  const visibleAttachments = useMemo(() => {
    if (!subItem?.attachments) return [];
    const removedIds = draft?.removedAttachmentIds || [];
    return subItem.attachments.filter((a) => !removedIds.includes(a.id));
  }, [subItem?.attachments, draft?.removedAttachmentIds]);

  // Unsaved changes detection
  const { confirmIfUnsaved, checkChanges } = useUnsavedChanges({
    draft,
    getFormData: form.getValues,
    pendingAttachmentCount: pendingAttachments.length,
    enabled: !saving,
    draftType: 'subItem',
  });

  // Track changes for Save button state (both create and edit modes)
  useEffect(() => {
    const result = checkChanges();
    setHasChanges(result.hasChanges);
  }, [checkChanges, draft, pendingAttachments.length]);

  // Also update hasChanges when form values change
  useEffect(() => {
    const subscription = form.watch(() => {
      const result = checkChanges();
      setHasChanges(result.hasChanges);
    });
    return () => subscription.unsubscribe();
  }, [form, checkChanges]);

  // Core save logic - returns true on success
  const performSave = async (data: ItemFormData): Promise<boolean> => {
    if (!user) return false;

    try {
      // Get organizationId from user profile (for org context)
      const organizationId = profile?.organizationId;

      // If no parentId, this is a pending sub-item - just return the input
      if (!parentId) {
        const input = formDataToCreateSubItemInput(data, currentType, organizationId);
        onSave(input);
        return true;
      }

      let subItemId = effectiveSubItemId;

      // Use parent's createdBy for Firestore path, fallback to user.uid
      const creatorId = parentCreatedBy || user.uid;

      if (effectiveSubItemId) {
        const updateInput = formDataToUpdateSubItemInput(data, currentType, organizationId);
        await editSubItem(creatorId, parentId, effectiveSubItemId, updateInput);
        toast.success(`${getItemTypeLabel(currentType)} updated`);
      } else {
        const createInput = formDataToCreateSubItemInput(data, currentType, organizationId);
        subItemId = await addSubItem(creatorId, parentId, createInput);
        setCreatedSubItemId(subItemId);
        toast.success(`${getItemTypeLabel(currentType)} created`);
      }

      // Delete removed attachments (deferred deletion)
      if (isEditing && subItem && draft && draft.removedAttachmentIds.length > 0) {
        for (const attachmentId of draft.removedAttachmentIds) {
          const attachment = subItem.attachments?.find((a) => a.id === attachmentId);
          if (attachment) {
            try {
              await removeSubItemAttachment(creatorId, parentId, subItem.id, attachment);
            } catch (err) {
              console.error('Failed to remove attachment:', err);
            }
          }
        }
      }

      // Upload pending attachments
      if (subItemId && organizationId) {
        for (const file of pendingAttachments) {
          await addSubItemAttachment(creatorId, parentId, subItemId, file, organizationId);
        }
      }

      setPendingAttachments([]);
      return true;
    } catch {
      toast.error(
        isEditing
          ? `Failed to update ${getItemTypeLabel(currentType).toLowerCase()}`
          : `Failed to create ${getItemTypeLabel(currentType).toLowerCase()}`
      );
      return false;
    }
  };

  // Save only (stay on page) - only for edit mode
  const handleSave = form.handleSubmit(async (data) => {
    setSaving(true);
    const success = await performSave(data);
    setSaving(false);

    // Reset draft with new original state (form data is now "saved" state)
    if (success && draft) {
      const newOriginalState = captureOriginalStateFromDefaults(data);
      const resetDraft: SubItemDraft = {
        ...draft,
        originalState: newOriginalState,
        removedAttachmentIds: [],
        lastModified: Date.now(),
      };
      setDraft(resetDraft);
      setHasChanges(false);
    }
  });

  // Save and close (navigate away)
  const handleSaveAndClose = form.handleSubmit(async (data) => {
    setSaving(true);
    const success = await performSave(data);
    setSaving(false);
    if (success) {
      onSave();
    }
  });

  const handleBack = useCallback(() => {
    confirmIfUnsaved(() => onBack(), {
      title: 'Unsaved Changes',
      onSave: handleSaveAndClose,
    });
  }, [confirmIfUnsaved, onBack, handleSaveAndClose]);

  // Handle delete button click
  const handleDelete = useCallback(() => {
    if (!subItem || !parentId) return;

    const creatorId = parentCreatedBy || user?.uid;
    if (!creatorId) return;

    confirmDelete({
      title: 'Delete Sub-item',
      message: `Are you sure you want to delete "${subItem.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await removeSubItem(creatorId, parentId, subItem.id);
          toast.success(`${getItemTypeLabel(currentType)} deleted`);
          onBack();
        } catch {
          toast.error(`Failed to delete ${getItemTypeLabel(currentType).toLowerCase()}`);
        }
      },
    });
  }, [subItem, parentId, parentCreatedBy, user?.uid, confirmDelete, removeSubItem, toast, currentType, onBack]);

  const title = isEditing
    ? `Edit ${getItemTypeLabel(currentType)}`
    : `New ${getItemTypeLabel(currentType)}`;

  // Header metadata: Created and Completed/Finalized timestamps
  const headerMetadata = useMemo(() => {
    if (!subItem) return null;

    const anyItem = subItem as unknown as Record<string, unknown>;
    const createdAt = formatEditorTimestamp(anyItem.createdAt as { toDate: () => Date } | undefined);
    const completedAt = subItem.type === 'task'
      ? formatEditorTimestamp(anyItem.completedAt as { toDate: () => Date } | undefined)
      : formatEditorTimestamp(anyItem.finalizedAt as { toDate: () => Date } | undefined);
    const completionLabel = subItem.type === 'task' ? 'Completed' : 'Finalized';

    if (!createdAt && !completedAt) return null;

    return (
      <span className="text-sm text-gray-500 ml-4">
        {createdAt && <span>Created: {createdAt}</span>}
        {createdAt && completedAt && <span className="mx-2">|</span>}
        {completedAt && <span>{completionLabel}: {completedAt}</span>}
      </span>
    );
  }, [subItem]);

  // Column 2 title varies by type
  const getCol2Title = () => {
    if (currentType === 'transaction') return 'Details and Dates';
    return 'Dates';
  };

  return (
    <div className="mb-[60px]">
      <ThreeColumnEditor
        title={title}
        titleBadge={
          <span className="px-2.5 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full border border-primary-200">
            Sub-item
          </span>
        }
        headerMetadata={headerMetadata}
        onBack={handleBack}
        onCancel={handleBack}
        onSave={handleSave}
        onSaveAndClose={handleSaveAndClose}
        saving={saving}
        saveDisabled={!hasChanges}
        onDelete={isEditing ? handleDelete : undefined}
        deleteDisabled={saving}
        leftTitle="Details"
        middleTitle={getCol2Title()}
        rightTitle="Attachments"
        leftPanel={
          <ItemFormFieldsCol1
            type={currentType}
            form={form}
            disabled={saving}
            statusOnlyEditable={isSubItemCompleted}
            isSubItem={true}
          />
        }
        middlePanel={
          <ItemFormFieldsCol2
            type={currentType}
            form={form}
            disabled={saving}
            statusOnlyEditable={isSubItemCompleted}
            isSubItem={true}
          />
        }
        rightPanel={
          <AttachmentsField
            existingAttachments={visibleAttachments}
            pendingFiles={pendingAttachments}
            onDrop={handleFileDrop}
            onRemoveExisting={handleRemoveExistingAttachment}
            onRemovePending={handleRemovePendingFile}
            disabled={saving || isSubItemCompleted}
          />
        }
      />
    </div>
  );
}
